/* eslint-disable no-bitwise */
import lineArc from '@turf/line-arc';
import { point } from '@turf/helpers';
import moment from 'moment-timezone';
import distance from '@turf/distance';
import humanizeDuration from 'humanize-duration';

export const mindlabs = window.location.hostname.includes('mindlabs');

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export const capitalize = (r = '') => r.charAt(0).toUpperCase() + r.slice(1);

export const isEmail = (email) => emailRegex.test(email);

export const isValidString = (str) => /^$|^[A-Za-z0-9_.\- ()]+$/.test(str);

export const getCircleCoordinates = (centerLonLat, radiusKm) => {
  const center = point(centerLonLat);
  const bearing1 = 0;
  const bearing2 = 360;
  const arc = lineArc(center, radiusKm, bearing1, bearing2); // 64 steps default
  return arc.geometry.coordinates;
};

export const radiusFromBbox = (bbox) => {
  if (!bbox || !bbox.length) return 25;
  const r = distance([bbox[0], bbox[1]], [bbox[2], bbox[3]], { units: 'kilometers' }) / 2;
  let n = 0;
  if (r > 100) n = 1;
  if (r > 1000) n = 2;
  return Math.round(r / (10 ** n)) * (10 ** n);
};

export const generateRandomId = (length) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';

  for (let i = 0; i < length; i += 1) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    id += characters.charAt(randomIndex);
  }

  return id;
};

export const isNum = (v) => !Number.isNaN(parseInt(v, 10));

export const parse = (v, float) => (isNum(v) ? (float ? Number.parseFloat(v, 10) : Number.parseInt(v, 10)) : NaN);

export const sec = (ts) => (ts < 9999999999 ? ts : Math.round(ts / 1000));

export const msec = (ts) => {
  const n = Number(ts);
  return n < 10000000000 ? n * 1000 : n || '';
};

export const format = (ts, f, tz) => (ts ? moment.tz(msec(ts), tz || localStorage.getItem('timezone') || moment.tz.guess()).format(f || 'YYYY-MM-DD HH:mm:ss') : '');

export const format1 = (ts, f, tz) => (ts ? moment.tz(msec(ts), tz || localStorage.getItem('timezone') || moment.tz.guess()).format(f || 'DD/MM/YYYY HH:mm:ss') : '');

export const tzFormat = (tz) => (tz ? moment.tz(tz || localStorage.getItem('timezone') || moment.tz.guess()).format('Z') : '');

export const round1Digit = (v) => (v ? Math.round(v * 10) / 10 : v);

export const unformatMacAddress = (mac) => {
  const macStr = mac?.toString?.() ?? '';
  return macStr.match(/.{1,2}/g).join(':').toUpperCase();
};

export const round5 = (v) => (isNum(v) ? Math.round(v * 100000) / 100000 : v);

// Which fridge each probe sits in is the operator's own label, so a dual-probe logger has up to
// three. Joining whatever is set beats a case per combination, which is what the two-alias
// version was and does not survive another probe.
export const deviceAliases = (aliases) => [aliases?.internal, aliases?.probe1, aliases?.probe2].filter(Boolean);

export const formatDeviceLabel = ((d) => {
  if (!d) return '';
  let label = d.id;
  if (d.personalReference) label = `${d.personalReference} | ${label}`;
  return [label, ...deviceAliases(d.aliases)].join(' | ');
});

export const maskString = (str, n = 0) => str.substring(0, n).padEnd(str.length, '•');

export const delHbyR = (83.144 / 8.314) * 1000;

export const timezones = moment.tz.names();

export const tzoffsets = {
  '-690': {
    offset: -690,
    label: 'UTC-11:30',
  },
  '-660': {
    offset: -660,
    label: 'UTC-11:00',
  },
  '-630': {
    offset: -630,
    label: 'UTC-10:30',
  },
  '-600': {
    offset: -600,
    label: 'UTC-10:00',
  },
  '-570': {
    offset: -570,
    label: 'UTC-09:30',
  },
  '-540': {
    offset: -540,
    label: 'UTC-09:00',
  },
  '-510': {
    offset: -510,
    label: 'UTC-08:30',
  },
  '-480': {
    offset: -480,
    label: 'UTC-08:00',
  },
  '-450': {
    offset: -450,
    label: 'UTC-07:30',
  },
  '-420': {
    offset: -420,
    label: 'UTC-07:00',
  },
  '-390': {
    offset: -390,
    label: 'UTC-06:30',
  },
  '-360': {
    offset: -360,
    label: 'UTC-06:00',
  },
  '-330': {
    offset: -330,
    label: 'UTC-05:30',
  },
  '-300': {
    offset: -300,
    label: 'UTC-05:00',
  },
  '-270': {
    offset: -270,
    label: 'UTC-04:30',
  },
  '-240': {
    offset: -240,
    label: 'UTC-04:00',
  },
  '-210': {
    offset: -210,
    label: 'UTC-03:30',
  },
  '-180': {
    offset: -180,
    label: 'UTC-03:00',
  },
  '-150': {
    offset: -150,
    label: 'UTC-02:30',
  },
  '-120': {
    offset: -120,
    label: 'UTC-02:00',
  },
  '-90': {
    offset: -90,
    label: 'UTC-01:30',
  },
  '-60': {
    offset: -60,
    label: 'UTC-01:00',
  },
  '-30': {
    offset: -30,
    label: 'UTC-00:30',
  },
  0: {
    offset: 0,
    label: 'UTC+00:00',
  },
  30: {
    offset: 30,
    label: 'UTC+00:30',
  },
  60: {
    offset: 60,
    label: 'UTC+01:00',
  },
  90: {
    offset: 90,
    label: 'UTC+01:30',
  },
  120: {
    offset: 120,
    label: 'UTC+02:00',
  },
  150: {
    offset: 150,
    label: 'UTC+02:30',
  },
  180: {
    offset: 180,
    label: 'UTC+03:00',
  },
  210: {
    offset: 210,
    label: 'UTC+03:30',
  },
  240: {
    offset: 240,
    label: 'UTC+04:00',
  },
  270: {
    offset: 270,
    label: 'UTC+04:30',
  },
  300: {
    offset: 300,
    label: 'UTC+05:00',
  },
  330: {
    offset: 330,
    label: 'UTC+05:30',
  },
  360: {
    offset: 360,
    label: 'UTC+06:00',
  },
  390: {
    offset: 390,
    label: 'UTC+06:30',
  },
  420: {
    offset: 420,
    label: 'UTC+07:00',
  },
  450: {
    offset: 450,
    label: 'UTC+07:30',
  },
  480: {
    offset: 480,
    label: 'UTC+08:00',
  },
  510: {
    offset: 510,
    label: 'UTC+08:30',
  },
  540: {
    offset: 540,
    label: 'UTC+09:00',
  },
  570: {
    offset: 570,
    label: 'UTC+09:30',
  },
  600: {
    offset: 600,
    label: 'UTC+10:00',
  },
  630: {
    offset: 630,
    label: 'UTC+10:30',
  },
  660: {
    offset: 660,
    label: 'UTC+11:00',
  },
  690: {
    offset: 690,
    label: 'UTC+11:30',
  },
};

export const checkFV = (fV, recommendedFV) => {
  if (!fV) return false;
  if (!fV.startsWith('v')) return true;
  const [a, b, c] = fV.split('.');
  const [a0, b0, c0] = recommendedFV.split('.');
  if (a > a0) return true; if (a < a0) return false;
  if (b > b0) return true; if (b < b0) return false;
  if (c > c0) return true; if (c < c0) return false;
  return true;
};

export const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});

export const humanizeInterval = (val) => {
  switch (Number(val)) {
    case '': return '';
    case 5: return '5 min';
    case 10: return '10 min';
    case 15: return '15 min';
    case 30: return '30 min';
    case 60: return '1 hr';
    case 120: return '2 hr';
    case 240: return '4 hr';
    case 360: return '6 hr';
    case 720: return '12 hr';
    default: return val ? `${val} min` : '-';
  }
};

export const formatGoConfig = (SI) => {
  if (!SI) return '-';
  if (Number(SI) === 60) return '60+ days';
  if (Number(SI) === 120) return '110+ days';
  if (Number(SI) === 240) return '200+ days';
  return `Custom (${humanizeInterval(SI)})`;
};

export const configStringToJSON = (str, keys) => {
  if (typeof str !== 'string' || !str) return null;
  const json = {};
  try {
    const dummyJson = JSON.parse(str);
    if (!keys) return dummyJson;
    Object.entries(dummyJson).forEach(([k, v]) => { if (keys.includes(k)) json[k] = v; });
  } catch (err) {
    const dummy = str.split(';');
    for (let i = 0; i < dummy.length - 1; i += 1) {
      const b = dummy[i].split(':'); const k = b[0]; const v = b[1] || null;
      if (!keys || keys.includes(k)) json[k] = v;
    }
  }
  return json;
};

export const configJSONToString = (json) => {
  if (typeof json !== 'object') return null;
  let str = '';
  Object.entries(json).forEach(([k, v]) => { str += `${k}:${v};`; });
  return str;
};

export const alarmLabelFormat = (level) => {
  return level;
};

export const getOptimalDurationChartData = (chartData, config) => {
  let highDuration = 0;
  let lowDuration = 0;
  const json = chartData.alarmsByDevice?.[config.DEVICE_ID]?.tempI;
  for (const setting of ['H1', 'H2', 'H3']) {
    if (isNum(json?.[setting]?.cumulativeOutOfRangeDuration)) {
      highDuration = json?.[setting]?.cumulativeOutOfRangeDuration;
      break;
    }
  }
  for (const setting of ['L1', 'L2', 'L3']) {
    if (isNum(json?.[setting]?.cumulativeOutOfRangeDuration)) {
      lowDuration = json?.[setting]?.cumulativeOutOfRangeDuration;
      break;
    }
  }
  return (
    (config?.NUM_DATA_POINTS_COLLECTED * config?.COLLECTION_INTERVAL - (highDuration + lowDuration)) * 1000
  );
};

export const formatBatteryVoltage = (vol) => {
  if (vol === undefined) return '-';
  if (!isNum(vol) || !(parse(vol) >= 0 && parse(vol) < 3400)) return 'ERR';
  if (vol >= 2900) return 'Good';
  return 'Average';
};

export const distanceBetweenTwoLocationPoints = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => deg * Math.PI / 180;

  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a = ((Math.sin(dLat / 2) ** 2) + ((Math.cos(toRad(lat1))) * (Math.cos(toRad(lat2))) * (Math.sin(dLon / 2) ** 2)));

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // distance in km

  return d;
};

// Light (alsLux) and shock (imuMagnitude) are missing from the sensor map of every logger attached
// before DeviceHub started listing them per device (May 2026), so they used to be exempted from the
// per-logger check and gated on the device type instead — which meant a ZN_GO logger always got a
// Light and a Shock panel (and a Light/Shock option in the report dialog) even with the sensor off and
// no such data in the range, while the alert side stayed correctly silent.
// Honour the toggle whenever the key exists, exactly as for a probe; only a legacy record with no key
// falls back to the device type, and then only if the range genuinely holds light/shock data.
// bl/reports/assets.ts deviceGraphsSensor() applies the same rule to the PDF — keep them in step.
const hasLightOrShockReading = (p, key) => (key === 'alsLux' ? p.alsStatus !== undefined : !!p.imuInterrupt);

export const deviceGraphsSensor = (device, key, seriesData) => {
  if (key !== 'alsLux' && key !== 'imuMagnitude') return !!device?.sensors?.[key];
  if (key in (device?.sensors || {})) return !!device.sensors[key];
  return !!device?.type?.startsWith('ZN_GO') && (seriesData || []).some((p) => hasLightOrShockReading(p, key));
};

export const anyDeviceGraphsSensor = (devices, key, seriesDataByDevice = {}) => (
  Object.entries(devices || {}).some(([id, d]) => deviceGraphsSensor(d, key, seriesDataByDevice[d?.id || id]))
);
